import type { PlatformChatHistoryTurn } from '@/apis/chat';

/** Product name for the clinician AI assistant, shown on the full page and the widget. */
export const PLATFORM_ASSISTANT_NAME = 'Telos';

/** Cap history turns sent to the API to keep payloads small and within model context. */
export const MAX_PLATFORM_CHAT_HISTORY = 10;

/** Model id the server reports for a reply (debug trace first, then top level), or null. */
export const platformChatModelFrom = (metadata?: Record<string, unknown> | null): string | null => {
  if (!metadata) return null;
  const debug = metadata.debug;
  if (
    debug &&
    typeof debug === 'object' &&
    typeof (debug as { model_id?: unknown }).model_id === 'string'
  ) {
    return (debug as { model_id: string }).model_id;
  }
  if (typeof metadata.model_id === 'string') return metadata.model_id;
  if (typeof metadata.model === 'string') return metadata.model;
  return null;
};

export const PLATFORM_CHAT_WIDGET_STORAGE_KEY = 'platform-chat-widget-messages';
export const PLATFORM_CHAT_PAGE_STORAGE_KEY = 'platform-chat-messages';
export const PLATFORM_CHAT_SESSION_STORAGE_KEY = 'telos-chat-session';

export type PlatformChatStoredMessage = {
  role: 'user' | 'bot';
  content: string;
  isError?: boolean;
};

/** The @mentioned patient the conversation is currently scoped to. */
export type PlatformChatMention = {
  userProfileId: string;
  display: string;
};

/**
 * Mention scope: the active mention plus the message-count boundary at which
 * the scope last changed. History sent to the API starts at the boundary so a
 * previous patient's turns are never paired with another patient's ID.
 */
export type PlatformChatMentionScope = {
  mention: PlatformChatMention | null;
  boundary: number;
};

export const EMPTY_MENTION_SCOPE: PlatformChatMentionScope = { mention: null, boundary: 0 };

const MENTION_STORAGE_SUFFIX = '-mention';

/** Trailing `@query` token before the end of the input, or null when absent. */
export const getTrailingMentionQuery = (text: string): string | null => {
  const match = /(?:^|\s)@([A-Za-z0-9._-]*)$/.exec(text);
  return match ? match[1] : null;
};

/** Replace the trailing `@query` token with the selected patient's display name. */
export const insertMentionIntoText = (text: string, display: string): string =>
  text.replace(/@[A-Za-z0-9._-]*$/, `@${display} `);

const isPlatformChatMention = (value: unknown): value is PlatformChatMention => {
  if (!value || typeof value !== 'object') return false;
  const mention = value as Record<string, unknown>;
  return (
    typeof mention.userProfileId === 'string' &&
    mention.userProfileId.trim().length > 0 &&
    typeof mention.display === 'string' &&
    mention.display.trim().length > 0
  );
};

const isPlatformChatMentionScope = (value: unknown): value is PlatformChatMentionScope => {
  if (!value || typeof value !== 'object') return false;
  const scope = value as Record<string, unknown>;
  return (
    typeof scope.boundary === 'number' &&
    Number.isSafeInteger(scope.boundary) &&
    scope.boundary >= 0 &&
    (scope.mention === null || isPlatformChatMention(scope.mention))
  );
};

export const loadPlatformChatMentionScope = (
  storageKey: string | null
): PlatformChatMentionScope => {
  if (!storageKey) return EMPTY_MENTION_SCOPE;
  try {
    const saved = localStorage.getItem(`${storageKey}${MENTION_STORAGE_SUFFIX}`);
    if (!saved) return EMPTY_MENTION_SCOPE;
    const parsed: unknown = JSON.parse(saved);
    if (isPlatformChatMentionScope(parsed)) return parsed;
    return EMPTY_MENTION_SCOPE;
  } catch {
    return EMPTY_MENTION_SCOPE;
  }
};

export const savePlatformChatMentionScope = (
  storageKey: string | null,
  scope: PlatformChatMentionScope
): void => {
  if (!storageKey) return;
  try {
    if (scope.mention || scope.boundary > 0) {
      localStorage.setItem(`${storageKey}${MENTION_STORAGE_SUFFIX}`, JSON.stringify(scope));
    } else {
      localStorage.removeItem(`${storageKey}${MENTION_STORAGE_SUFFIX}`);
    }
  } catch {
    /* quota exceeded */
  }
};

export const getPlatformChatStorageKey = (
  baseKey: string,
  userId: string | null | undefined
): string | null => (userId ? `${baseKey}-${userId}` : null);

const isPlatformChatStoredMessage = (value: unknown): value is PlatformChatStoredMessage => {
  if (!value || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  return (
    (msg.role === 'user' || msg.role === 'bot') &&
    typeof msg.content === 'string' &&
    (msg.isError === undefined || typeof msg.isError === 'boolean')
  );
};

export const loadPlatformChatMessages = <T extends PlatformChatStoredMessage>(
  storageKey: string | null
): T[] => {
  if (!storageKey) return [];
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return [];
    const parsed: unknown = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPlatformChatStoredMessage) as T[];
  } catch {
    return [];
  }
};

export const savePlatformChatMessages = (storageKey: string | null, messages: unknown[]): void => {
  if (!storageKey) return;
  try {
    if (messages.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch {
    /* quota exceeded */
  }
};

export type PlatformChatStoredSession<T extends PlatformChatStoredMessage> = {
  messages: T[];
  scope: PlatformChatMentionScope;
};

const emptyStoredSession = <
  T extends PlatformChatStoredMessage,
>(): PlatformChatStoredSession<T> => ({
  messages: [],
  scope: EMPTY_MENTION_SCOPE,
});

const validatedSessionScope = (scope: unknown, messageCount: number): PlatformChatMentionScope =>
  isPlatformChatMentionScope(scope) && scope.boundary <= messageCount
    ? scope
    : { mention: null, boundary: messageCount };

const clearPreviousPlatformChatKeys = (userId: string): void => {
  for (const baseKey of [PLATFORM_CHAT_PAGE_STORAGE_KEY, PLATFORM_CHAT_WIDGET_STORAGE_KEY]) {
    const key = getPlatformChatStorageKey(baseKey, userId)!;
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}${MENTION_STORAGE_SUFFIX}`);
  }
};

export const savePlatformChatSession = <T extends PlatformChatStoredMessage>(
  userId: string | null,
  session: PlatformChatStoredSession<T>
): boolean => {
  const key = getPlatformChatStorageKey(PLATFORM_CHAT_SESSION_STORAGE_KEY, userId);
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify({ version: 1, ...session }));
    return true;
  } catch {
    return false;
  }
};

export const loadPlatformChatSession = <T extends PlatformChatStoredMessage>(
  userId: string | null
): PlatformChatStoredSession<T> => {
  const key = getPlatformChatStorageKey(PLATFORM_CHAT_SESSION_STORAGE_KEY, userId);
  if (!key || !userId) return emptyStoredSession<T>();
  try {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      const parsed: unknown = JSON.parse(saved);
      if (!parsed || typeof parsed !== 'object') return emptyStoredSession<T>();
      const stored = parsed as Record<string, unknown>;
      if (
        stored.version !== 1 ||
        !Array.isArray(stored.messages) ||
        !stored.messages.every(isPlatformChatStoredMessage)
      ) {
        return emptyStoredSession<T>();
      }
      const session = {
        messages: stored.messages as T[],
        scope: validatedSessionScope(stored.scope, stored.messages.length),
      };
      return session;
    }

    const candidates = [PLATFORM_CHAT_PAGE_STORAGE_KEY, PLATFORM_CHAT_WIDGET_STORAGE_KEY].map(
      (baseKey) => {
        const previousKey = getPlatformChatStorageKey(baseKey, userId)!;
        const savedMessages = localStorage.getItem(previousKey);
        if (!savedMessages) return null;
        let parsedMessages: unknown;
        let parsedScope: unknown = null;
        try {
          parsedMessages = JSON.parse(savedMessages);
        } catch {
          return null;
        }
        try {
          parsedScope = JSON.parse(
            localStorage.getItem(`${previousKey}${MENTION_STORAGE_SUFFIX}`) ?? 'null'
          );
        } catch {
          parsedScope = null;
        }
        if (!Array.isArray(parsedMessages) || !parsedMessages.every(isPlatformChatStoredMessage)) {
          return null;
        }
        const latestAt = parsedMessages.reduce((latest, message) => {
          const value = (message as { created_at?: unknown }).created_at;
          const timestamp = typeof value === 'string' ? Date.parse(value) : NaN;
          return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest;
        }, 0);
        return {
          messages: parsedMessages as T[],
          scope: validatedSessionScope(parsedScope, parsedMessages.length),
          latestAt,
        };
      }
    );
    const selected = candidates.reduce<(typeof candidates)[number]>(
      (latest, candidate) =>
        candidate &&
        (!latest ||
          candidate.latestAt > latest.latestAt ||
          (latest.messages.length === 0 && candidate.messages.length > 0))
          ? candidate
          : latest,
      null
    );
    const session = selected
      ? { messages: selected.messages, scope: selected.scope }
      : emptyStoredSession<T>();
    savePlatformChatSession(userId, session);
    return session;
  } catch {
    return emptyStoredSession<T>();
  }
};

/** Remove legacy unscoped keys so prior sessions cannot leak across users. */
export const clearLegacyPlatformChatStorage = (): void => {
  try {
    for (const key of [
      PLATFORM_CHAT_WIDGET_STORAGE_KEY,
      PLATFORM_CHAT_PAGE_STORAGE_KEY,
      PLATFORM_CHAT_SESSION_STORAGE_KEY,
    ]) {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}${MENTION_STORAGE_SUFFIX}`);
    }
  } catch {
    /* ignore */
  }
};

/** Wipe widget + full-page transcripts for a user (e.g. on logout). */
export const clearPlatformChatStorageForUser = (userId: string | null | undefined): void => {
  if (!userId) return;
  try {
    clearPreviousPlatformChatKeys(userId);
    localStorage.removeItem(getPlatformChatStorageKey(PLATFORM_CHAT_SESSION_STORAGE_KEY, userId)!);
  } catch {
    return;
  }
};

export const buildPlatformChatHistory = (
  messages: PlatformChatStoredMessage[],
  limit = MAX_PLATFORM_CHAT_HISTORY
): PlatformChatHistoryTurn[] =>
  messages
    .filter((m) => !m.isError)
    .slice(-limit)
    .map((m) => ({ role: m.role, content: m.content }));

/**
 * Compare the patient we asked about against the one the server says it scoped
 * the answer to (`metadata.patient_scope`), returning true when they disagree.
 *
 * A missing echo is not a mismatch: it is indistinguishable from an older API
 * build that does not send one, so the check degrades to unverified rather than
 * blocking every answer during a rollout.
 */
export const isPatientScopeMismatch = (
  requestedUserProfileId: string | undefined,
  metadata: Record<string, unknown> | undefined
): boolean => {
  const scope = metadata?.patient_scope;
  if (!scope || typeof scope !== 'object') return false;

  const echoedId = (scope as { user_profile_id?: unknown }).user_profile_id;
  if (typeof echoedId !== 'string' || !echoedId) return false;

  return echoedId.trim().toLowerCase() !== (requestedUserProfileId ?? '').trim().toLowerCase();
};

/** True for in-app paths like `/users/new`; false for external or protocol-relative URLs. */
export const isInternalAppPath = (href: string | undefined): href is string =>
  typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
